import { useCallback, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  QueueTeacherSmsMutationVariables,
  TeacherSmsStateDocument,
  TeacherSmsStateFieldsFragment,
  TeacherSmsStateQuery,
  TeacherSmsStateQueryVariables,
  useQueueTeacherSmsMutation,
  useTeacherSmsPreviewQuery,
  useTeacherSmsStateQuery,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import {
  ISmsLinkDraft,
  isSmsInFlight,
  SMS_STATE_POLL_MS,
  smsLinkInput,
} from './smsModel';
import { smsPermissionsFor } from './useSmsPermissions';
import { useSmsPreviewInput } from './useSmsPreviewInput';
import type {
  IStudentSmsSendInput,
  IStudentSmsSendResult,
  IUseSmsPreview,
} from './useStudentSms';

/**
 * SMS преподавателю из его карточки (2026-09-22) — зеркало `useStudentSms`:
 * можно ли, куда уйдёт, какие документы ждут его подписи (цель ссылки
 * «на подпись»), история его SMS, живой предпросмотр и отправка.
 */

export type ITeacherSmsState = TeacherSmsStateFieldsFragment;
export type ITeacherSignDocument = TeacherSmsStateFieldsFragment['documents'][number];

/**
 * Роли SMS преподавателю живут в `useSmsPermissions` (`TEACHER_SMS_ROLES`)
 * рядом с остальными: один файл на все три правила — раздел, студент,
 * преподаватель, — чтобы им некуда было разойтись.
 *
 * Можно ли SMS преподавателю: право на SMS И роль карточки преподавателя.
 */
export const canSmsTeacherFor = (roles: UserRole[]): boolean =>
  smsPermissionsFor(roles).canSendTeacher;

export const useCanSmsTeacher = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  return canSmsTeacherFor((userState.me?.roles ?? []) as UserRole[]);
};

export const useTeacherSmsState = (teacherId: string, enabled = true) => {
  const canUse = useCanSmsTeacher();
  const skip = !canUse || !enabled || !teacherId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useTeacherSmsStateQuery({
    variables: { teacherId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const state = data?.teacherSms ?? null;
  // Опрос — только пока что-то в пути (как у студента).
  const inFlight = Boolean(state && isSmsInFlight(state.messages));
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    SMS_STATE_POLL_MS,
    !skip && inFlight,
  );
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    canUse,
    state,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

/**
 * Живой предпросмотр SMS преподавателю. `network-only` и прошлый ответ до
 * нового — по той же причине, что у студента (`useStudentSmsPreview`).
 */
export const useTeacherSmsPreview = (
  teacherId: string,
  text: string,
  link: ISmsLinkDraft,
  enabled = true,
): IUseSmsPreview => {
  const canUse = useCanSmsTeacher();
  const { debounced, lagging, hasText } = useSmsPreviewInput(text, link);
  const skip = !canUse || !enabled || !teacherId || !debounced.text;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useTeacherSmsPreviewQuery({
    variables: { teacherId, text: debounced.text, link: debounced.link },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  if (!hasText) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewTeacherSms ?? null;
  return {
    preview: current,
    pending: lagging || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export type ITeacherSmsSendInput = IStudentSmsSendInput;
export type ITeacherSmsSendResult = IStudentSmsSendResult;

/**
 * Отправить SMS преподавателю. Ответ несёт полное состояние — оно пишется в
 * запрос карточки (не `refetchQueries` по имени — грабли
 * `refetch-by-name-skipped-query`).
 */
export const useTeacherSmsSend = (teacherId: string) => {
  const [sendMut, { loading: sending }] = useQueueTeacherSmsMutation();
  const inFlight = useRef(false);

  const send = useCallback(
    async (input: ITeacherSmsSendInput): Promise<ITeacherSmsSendResult> => {
      if (inFlight.current) return { ok: false, errors: [], status: null };
      inFlight.current = true;
      try {
        const variables: QueueTeacherSmsMutationVariables = {
          teacherId,
          text: input.text.trim(),
          link: smsLinkInput(input.link),
          templateId: input.templateId || null,
          requestKey: input.requestKey,
        };
        const { data } = await sendMut({
          variables,
          update: (cache, { data: answer }) => {
            const state = answer?.sendTeacherSms?.state;
            if (!state) return;
            cache.writeQuery<
              TeacherSmsStateQuery,
              TeacherSmsStateQueryVariables
            >({
              query: TeacherSmsStateDocument,
              variables: { teacherId },
              data: { teacherSms: state },
            });
          },
        });
        const payload = data?.sendTeacherSms;
        const errors: ISmsError[] = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          status: payload?.message?.status ?? null,
          body: payload?.message?.body ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), status: null };
      } finally {
        inFlight.current = false;
      }
    },
    [sendMut, teacherId],
  );

  return { send, sending };
};
